import {
  Form,
  Image,
  Input,
  Modal,
  Space,
  Typography,
  Upload,
  Collapse,
  Select,
  Row,
  Col
} from 'antd';
import { FC, memo, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalStateContext, useUIDispatchContext } from 'providers/UI';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { useFileUpload } from 'hooks';
import { useAuthStateContext } from 'providers/Auth';
import {
  createSpecialty,
  fetchSpecialties,
  fetchSpecialtyById,
  updateSpecialty
} from 'services/api/specialties';
import {
  SpecialtyInput,
  SpecialtyListItem,
  SpecialtyTranslationInput
} from 'types/api/specialties';

import { showErrorMessage } from 'utils/showErrorMessage';
import { Loading } from 'components/ui';
import { getFileUrl } from 'utils/file';

const { TextArea } = Input;
const { Text } = Typography;

const itemValues = {
  name: '',
  description: '',
  short_description: ''
};

type ValuesType = typeof itemValues;
type AdditionalTypes = {
  visible: boolean;
  symptom: boolean;
  parents?: number[];
};
type BodyType = Record<string, ValuesType>;

const { Panel } = Collapse;

interface IProps {
  refetch(): void;
}

const SpecialtyModal: FC<IProps> = ({ refetch }) => {
  // translations
  const { t } = useTranslation();
  // form
  const [form] = Form.useForm();
  // ui
  const { toggleModal } = useUIDispatchContext();
  const { specialtyModal } = useModalStateContext();

  const specialtyId = specialtyModal.params?.id;

  // state
  const [icon, setIcon] = useState('');
  const [image, setImage] = useState('');
  const [errorKeys, setErrorKeys] = useState<string[]>([]);
  // AWS
  const { upload } = useFileUpload();

  const { languages } = useAuthStateContext();

  const closeModal = () => {
    toggleModal('specialtyModal', false);
    form.resetFields();
    setIcon('');
    setImage('');
    setErrorKeys([]);
  };

  const [specialties, setSpecialties] = useState<SpecialtyListItem[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!specialtyModal.visible) return;
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetchSpecialties({ lang: 'en', symptom: true });
        if (!cancelled) setSpecialties(res);
      } catch {
        // swallow — empty parent options on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [specialtyModal.visible]);

  useEffect(() => {
    if (!specialtyId) return;
    let cancelled = false;

    const load = async () => {
      try {
        setIsFetching(true);
        const data = await fetchSpecialtyById(specialtyId);
        if (cancelled) return;

        const values = (data.translates ?? []).reduce(
          (acc: BodyType, val) => ({
            ...acc,
            [val.lang]: {
              name: val.name ?? '',
              description: val.description ?? '',
              short_description: val.short_description ?? ''
            }
          }),
          {}
        );

        form.setFieldsValue({
          visible: data.visible,
          image: data.image,
          symptom: data.symptom,
          parents: data.parents?.map(p => p.parent_id),
          icon: data.icon,
          ...values
        });
        setIcon(data.icon || '');
        setImage(data.image || '');
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [specialtyId, form]);

  const onFinish = useCallback(
    async ({
      visible,
      parents,
      symptom,
      ...values
    }: AdditionalTypes & BodyType) => {
      setErrorKeys([]);

      // BE wants locale keys re-nested under `translations`; the FE form
      // still produces them at the top level alongside visible/symptom/parents.
      const translations = values as unknown as BodyType as Record<
        string,
        SpecialtyTranslationInput
      >;

      const input: SpecialtyInput = {
        image: image || undefined,
        icon: icon || undefined,
        visible: visible || false,
        symptom: symptom || false,
        parents: parents ?? null,
        translations
      };

      try {
        setIsSaving(true);

        if (specialtyId) {
          await updateSpecialty(specialtyId, input);
        } else {
          await createSpecialty(input);
        }

        refetch();
        closeModal();
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setIsSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [image, icon, specialtyId, refetch]
  );

  const onFinishError = (
    errorInfo: ValidateErrorEntity<
      {
        visible: boolean;
      } & BodyType
    >
  ) => {
    setErrorKeys([]);
    const errors = new Set(
      errorInfo.errorFields.map(item => `${item.name[0]}`)
    );

    setErrorKeys(Array.from(errors));
  };

  const results = specialties.filter(
    item => String(item.id) !== String(specialtyId)
  );

  const isSymptom = Form.useWatch('symptom', form);

  const hasAllLanguagesAccess = languages.every(item => item.allowEdit);

  return (
    <Modal
      title={
        <Space>{t(specialtyId ? 'common.edit' : 'specialty.add_new')} </Space>
      }
      onCancel={closeModal}
      open={specialtyModal.visible}
      width={600}
      onOk={form.submit}
      confirmLoading={isSaving}
      destroyOnClose
    >
      {isFetching ? (
        <Loading />
      ) : (
        <Form
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishError}
          layout="vertical"
          size="large"
        >
          <div>
            <Space>
              <Form.Item label={t('specialty.icon')}>
                <Upload
                  customRequest={e =>
                    upload(e.file as File).then(val => setIcon(val || ''))
                  }
                  multiple={false}
                  maxCount={1}
                  showUploadList={false}
                  disabled={!hasAllLanguagesAccess}
                >
                  <Image
                    src={getFileUrl(icon)}
                    width={100}
                    height={100}
                    preview={false}
                  />
                </Upload>
              </Form.Item>
              <Form.Item label={t('specialty.image')}>
                <Upload
                  customRequest={e =>
                    upload(e.file as File).then(val => setImage(val || ''))
                  }
                  multiple={false}
                  maxCount={1}
                  showUploadList={false}
                  disabled={!hasAllLanguagesAccess}
                >
                  <Image
                    src={getFileUrl(image)}
                    width={100}
                    height={100}
                    preview={false}
                  />
                </Upload>
              </Form.Item>
            </Space>
          </div>
          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item name="visible" label="Visibility">
                <Select
                  options={[
                    {
                      label: 'Visible',
                      value: true
                    },
                    {
                      label: 'Hidden',
                      value: false
                    }
                  ]}
                  disabled={!hasAllLanguagesAccess}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="symptom"
                label="Type"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    {
                      label: 'Specialty',
                      value: false
                    },
                    {
                      label: 'Symptom',
                      value: true
                    }
                  ]}
                  disabled={!hasAllLanguagesAccess}
                />
              </Form.Item>
            </Col>
          </Row>
          {!isSymptom && (
            <Form.Item
              name="parents"
              label="Symptoms"
              rules={[{ required: true }]}
            >
              <Select
                mode="multiple"
                options={results?.map(item => ({
                  label: item.name,
                  value: item.id
                }))}
                allowClear
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                disabled={!hasAllLanguagesAccess}
              />
            </Form.Item>
          )}
          <Collapse
            accordion
            defaultActiveKey={languages[0].locale}
            items={languages.map(item => {
              const hasError = errorKeys.includes(item.locale);

              return {
                key: item.key,
                label: item.label,
                forceRender: true,
                children: (
                  <Panel
                    header={
                      <Text
                        style={{
                          color: `var(--${
                            hasError ? 'error-color' : 'text-color'
                          })`
                        }}
                      >
                        {item.label}
                        {hasError ? ' (error)' : ''}
                      </Text>
                    }
                    key={item.locale}
                    forceRender
                  >
                    <Form.Item
                      name={[item.locale, 'name']}
                      label={t('specialty.name')}
                      rules={[{ required: true }]}
                    >
                      <Input disabled={!item.allowEdit} />
                    </Form.Item>
                    <Form.Item
                      name={[item.locale, 'short_description']}
                      label={t('specialty.short_description')}
                      rules={[
                        { required: false },
                        {
                          max: 165,
                          message: 'Value should be less than 50 character'
                        }
                      ]}
                    >
                      <Input disabled={!item.allowEdit} />
                    </Form.Item>
                    <Form.Item
                      name={[item.locale, 'description']}
                      label={t('specialty.description')}
                      rules={[{ required: false }]}
                    >
                      <TextArea disabled={!item.allowEdit} />
                    </Form.Item>
                  </Panel>
                )
              };
            })}
          />
        </Form>
      )}
    </Modal>
  );
};

export default memo(SpecialtyModal);
